import { useField } from "formik"
import { makeStyles } from "tss-react/mui"
import { enqueueSnackbar } from "notistack"
import { Stack, Typography } from "@mui/material"
import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { MonetaryAmount } from "../../../../../../modules/currency/currencyTypes"
import { DeliveryTimesField, DynamicConfigKey } from "../../form/formTypes"
import { StandardDialog } from "../../../../dialogs/StandardDialog"
import { ProductWishForm } from "./ProductWishForm"

const useStyles = makeStyles()((theme) => ({
    container: {
        padding: "0 8rem 3rem",
    },
    message: {
        color: theme.palette.tailwind.blue[950],
        background: theme.palette.tailwind.sky[100],
        borderRadius: ".5rem",
        padding: "1rem 2rem",
        fontWeight: "500",
    },
}))

export type ProductWishItem = Readonly<{
    name: string
    quantity: number
    price?: MonetaryAmount
}>

type ProductWishDialogProps = Readonly<{
    open: boolean
    onClose: () => void
}>

export function ProductWishDialog({ open, onClose }: ProductWishDialogProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    const [{ value: deliveries }, , deliveriesHelpers] = useField<DeliveryTimesField>(
        DynamicConfigKey.MULTIPLE_DELIVERIES,
    )

    const handleAddProductWish = (item: ProductWishItem) => {
        const activeTime = deliveries.activeTime
        if (!activeTime) {
            enqueueSnackbar(l10n.getString("create-order-delivery-times-empty-error"), { variant: "error" })
            return
        }
        const updatedDeliveries = {
            ...deliveries,
            deliveryTimes: deliveries.deliveryTimes.map((delivery) => {
                if (delivery.time === activeTime) {
                    const newOrderLines = [
                        ...delivery.orderLines,
                        {
                            type: DynamicConfigKey.PRODUCT_WISH,
                            id: `${activeTime}_${delivery.orderLines.length}_${item.name}`,
                            ...item,
                        },
                    ]

                    return { ...delivery, orderLines: newOrderLines }
                }
                return delivery
            }),
        }

        deliveriesHelpers.setValue(updatedDeliveries as DeliveryTimesField)
        onClose()
    }

    return (
        <LocalizedStrict id="create-order-wish-dialog-title" attrs={{ dialogTitle: true }}>
            <StandardDialog
                dialogTitle="Add product wish"
                open={open}
                onClose={onClose}
                maxWidthProp="md"
                scroll="body"
            >
                <Stack gap="1.5rem" className={classes.container}>
                    <LocalizedStrict id="create-order-wish-dialog-message">
                        <Typography variant="body1" className={classes.message}>
                            If you add a product wish, your order needs to be manually approved by the merchant.
                        </Typography>
                    </LocalizedStrict>
                    <ProductWishForm onSubmit={handleAddProductWish} />
                </Stack>
            </StandardDialog>
        </LocalizedStrict>
    )
}
